import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { formatShortDate, relativeDayLabel } from "../lib/dateFormat";

export const WeekSelector = ({ selectedDay, onSelectDay }) => {
  const [weekData, setWeekData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/weekly/predictions?start_offset=0&end_offset=6`)
      .then((res) => setWeekData(res.data))
      .catch(() => setWeekData({ days: [] }));
  }, []);

  const days = weekData?.days || [];

  return (
    <div
      className="nd-card p-4"
      data-testid="week-selector"
    >
      {weekData?.total_matches != null && (
        <div className="flex items-center justify-between mb-3">
          <span className="eyebrow">7 days</span>
          <span className="text-[11px] font-tabular text-muted-foreground">
            {weekData.total_matches} fixtures · {weekData.big_league_total} big-league
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {days.length === 0 && (
          <div className="col-span-full text-center text-sm text-muted-foreground py-3">
            Weekly fixtures syncing — check back shortly.
          </div>
        )}
        {days.map((d) => {
          // Iter 134h — Re-derive the label from the user's LOCAL
          // calendar so "Today" / "Tomorrow" / "Yesterday" match what
          // the user perceives (the backend label is UTC-anchored).
          const localLabel = relativeDayLabel(d.date);
          // Top pill: short "Today" / "Tomorrow" / "Mon" — extract
          // only the weekday from full labels like "Mon, May 18".
          const topLabel = localLabel.includes(",")
            ? localLabel.split(",")[0]
            : localLabel;
          const isSelected =
            selectedDay === topLabel.toLowerCase() ||
            (selectedDay === "today" && topLabel === "Today") ||
            (selectedDay === "tomorrow" && topLabel === "Tomorrow");
          return (
            <button
              key={d.date}
              onClick={() => onSelectDay && onSelectDay(d.date, topLabel)}
              data-testid={`week-day-${d.date}`}
              className={`group rounded-xl px-3 py-3 text-left transition-colors border ${
                isSelected
                  ? "bg-foreground text-background border-foreground"
                  : "bg-subtle border-border hover:border-foreground/40"
              }`}
            >
              <div className={`text-[10px] uppercase tracking-[0.12em] font-bold ${
                isSelected ? "text-background/80" : "text-muted-foreground"
              }`}>
                {topLabel}
              </div>
              <div className={`text-xs font-tabular mt-0.5 ${
                isSelected ? "text-background/80" : "text-muted-foreground"
              }`}>
                {formatShortDate(d.date)}
              </div>
              <div className="mt-2 flex items-baseline justify-between">
                <span className={`font-display text-2xl font-bold font-tabular ${
                  isSelected ? "text-background" : "text-foreground"
                }`}>
                  {d.match_count}
                </span>
                <span className={`text-[10px] font-medium ${
                  isSelected ? "text-background/70" : "text-muted-foreground"
                }`}>
                  games
                </span>
              </div>
              {d.big_league_count > 0 && (
                <div className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold ${
                  isSelected ? "text-background/90" : "text-primary"
                }`}>
                  <span className={`w-1 h-1 rounded-full ${isSelected ? "bg-background" : "bg-primary"}`} />
                  {d.big_league_count} big-league
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default WeekSelector;
